import { useState, useEffect } from "react";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { useForm } from "react-hook-form";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { toast } from "sonner";
import { institutionsAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { Home, Building2 } from "lucide-react";
import { cn } from "../lib/utils";
import { INSTITUTION_ONBOARDING_STEPS } from "../constants/institutionFormFields";
import InstitutionOnboardingForm from "../components/forms/InstitutionOnboardingForm";

interface InstitutionOnboardingProps {
  isModal?: boolean;
  institutionId?: string | number | null;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function InstitutionOnboarding({
  isModal = false,
  institutionId = null,
  onSuccess,
  onCancel,
}: InstitutionOnboardingProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<any>({});
  const MAX_INSTITUTION_IMAGES = 15;
  const [institutionImages, setInstitutionImages] = useState<File[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [brochureFile, setBrochureFile] = useState<File | null>(null);
  const [existingBrochureUrl, setExistingBrochureUrl] = useState<string>("");
  const navigate = usePrefixedNavigate();
  const { user } = useAuth();
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadingInitialData, setLoadingInitialData] = useState(false);

  const form = useForm();

  useEffect(() => {
    if (!user && !isModal) {
      navigate("/auth?portal=institution");
    }
  }, [user, navigate, isModal]);

  // Fetch data if in edit mode
  useEffect(() => {
    if (institutionId) {
      setIsEditMode(true);
      const fetchData = async () => {
        setLoadingInitialData(true);
        try {
          const result = await institutionsAPI.get(String(institutionId));
          const data = result as any;

          // Map educational_year fields if they come from old structure or to ensure consistency
          if (Array.isArray(data.educational_year)) {
            data.educational_year = data.educational_year.map((item: any) => ({
              educational_year: String(item?.educational_year ?? ""),
              total_capacity: Number(item?.total_capacity ?? 0),
              vacant: Number(item?.vacant ?? 0),
              occupied: Number(item?.occupied ?? 0),
              _id: item?._id,
            }));
          }

          // Pre-fill form state
          setFormData(data);

          // Pre-fill react-hook-form
          form.reset(data);

          // Handle specialized fields
          if (Array.isArray(data.departments)) {
            setSelectedDepartments(data.departments);
          }
          if (Array.isArray(data.facilities)) {
            setSelectedFacilities(data.facilities);
          }
          if (Array.isArray(data.levels)) {
            setSelectedLevels(data.levels);
          }

          // Load existing images from API
          const imagesArr = data.images ?? data.image;
          if (Array.isArray(imagesArr)) {
            setExistingImageUrls(
              imagesArr
                .map((u: any) => (typeof u === "string" ? u : (u?.url ?? "")))
                .filter(Boolean)
            );
          } else {
            setExistingImageUrls([]);
          }

          // Handle brochure
          if (data.brochure && typeof data.brochure === "string") {
            setExistingBrochureUrl(data.brochure);
          } else {
            setExistingBrochureUrl("");
          }
        } catch (error) {
          console.error("Error fetching institution data:", error);
          toast.error("Failed to fetch institution data");
        } finally {
          setLoadingInitialData(false);
        }
      };
      fetchData();
    }
  }, [institutionId, form]);

  const handleNext = (data: any) => {
    const merged = { ...formData, ...data };

    // Map educational_year in merged data to ensure consistency across steps
    if (Array.isArray(merged.educational_year)) {
      merged.educational_year = merged.educational_year.map((item: any) => ({
        educational_year: String(item?.educational_year ?? ""),
        total_capacity: Number(item?.total_capacity ?? 0),
        vacant: Number(item?.vacant ?? 0),
        occupied: Number(item?.occupied ?? 0),
        _id: item?._id,
      }));
    }

    setFormData(merged);
    const nextStep = step + 1;
    console.log(
      `[Institution Onboarding] Step ${step} completed — data:`,
      data
    );
    console.log(
      `[Institution Onboarding] Accumulated formData after step ${step}:`,
      merged
    );
    console.log(`[Institution Onboarding] Moving to step ${nextStep}`);
    form.reset(merged);
    setStep(nextStep);
  };

  const handleBack = () => {
    console.log(
      `[Institution Onboarding] Going back from step ${step} to step ${step - 1}`
    );
    setStep(step - 1);
    form.reset(formData);
  };

  const handleFinalSubmit = async (data: any) => {
    const rawEducationalYear =
      data.educational_year ?? formData.educational_year;
    const educational_year =
      Array.isArray(rawEducationalYear) && rawEducationalYear.length > 0
        ? rawEducationalYear.map((item: any) => ({
            educational_year: String(item?.educational_year ?? ""),
            total_capacity: Number(item?.total_capacity ?? 0),
            vacant: Number(item?.vacant ?? 0),
            occupied: Number(item?.occupied ?? 0),
          }))
        : [
            {
              educational_year: "2026",
              total_capacity: 100,
              vacant: 100,
              occupied: 0,
            },
          ];
    const finalData = {
      ...formData,
      ...data,
      educational_year,
      selectedDepartments,
      selectedFacilities,
      selectedLevels,
      image: institutionImages,
      images: existingImageUrls,
      brochure: brochureFile ?? existingBrochureUrl ?? null,
    };

    try {
      if (!user && !isModal) {
        toast.error("User not authenticated");
        return;
      }

      let result: any;
      if (isEditMode && institutionId) {
        if (institutionImages.length > 0) {
          result = await institutionsAPI.updateWithImages(
            String(institutionId),
            finalData,
            institutionImages
          );
        } else {
          result = await institutionsAPI.update(
            String(institutionId),
            finalData
          );
        }
      } else {
        result = await institutionsAPI.create(finalData, institutionImages);
      }

      if (!result) {
        toast.error(
          "Operation could not be completed. Please try again or contact support."
        );
        return;
      }

      const statusCode = result.status_code;
      const isSuccessStatus =
        statusCode === 201 ||
        Number(statusCode) === 201 ||
        statusCode === 200 ||
        Number(statusCode) === 200;
      const hasData =
        result.data !== undefined ||
        (result as { id?: unknown }).id !== undefined;
      const isSuccessMessage =
        typeof result.message === "string" &&
        /created successfully|success|updated successfully/i.test(
          result.message
        );

      const isSuccess = isSuccessStatus || hasData || isSuccessMessage;

      if (isSuccess) {
        const successMessage =
          result.message ??
          (isEditMode
            ? "Institution updated successfully."
            : "Your institution registration is pending approval.");
        toast.success(successMessage);

        if (onSuccess) {
          onSuccess();
        } else {
          navigate("/institution-admin/dashboard");
        }
      } else {
        toast.error(result.message ?? "Failed to save institution");
      }
    } catch (error: any) {
      console.error("Error:", error);
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.message ??
        "Failed to save institution";
      toast.error(errorMessage);
    }
  };

  const currentStepInfo = INSTITUTION_ONBOARDING_STEPS.find(
    s => s.step === step
  );

  if (loadingInitialData) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col bg-gradient-to-br from-background via-muted/30 to-background",
        !isModal && "min-h-screen"
      )}
    >
      {/* Header */}
      {!isModal && (
        <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-sm">
          <div className="container mx-auto flex items-center justify-between px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
                <Home className="h-5 w-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">
                  JBF Portal
                </h1>
                <p className="text-xs text-muted-foreground">
                  Institution Onboarding
                </p>
              </div>
            </div>
          </div>
        </header>
      )}

      <main className={cn("flex-1 px-4 py-8", isModal && "p-0")}>
        <div className={cn("mx-auto", !isModal ? "max-w-4xl" : "max-w-full")}>
          <Card className={cn(isModal && "border-0 shadow-none")}>
            <CardHeader className={isModal ? "px-0 pt-0" : ""}>
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-600">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <CardTitle>
                    {isEditMode
                      ? "Edit Institution"
                      : "Institution Registration"}
                  </CardTitle>
                  <CardDescription>
                    Step {step} of 5 - {currentStepInfo?.title}
                  </CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                {INSTITUTION_ONBOARDING_STEPS.map(s => (
                  <div
                    key={s.step}
                    className={`h-2 flex-1 rounded-full transition-colors ${
                      s.step <= step ? "bg-primary" : "bg-muted"
                    }`}
                  />
                ))}
              </div>
            </CardHeader>
            <CardContent className={isModal ? "px-0 pb-0" : ""}>
              <InstitutionOnboardingForm
                step={step}
                form={form}
                onNext={handleNext}
                onBack={handleBack}
                onFinalSubmit={handleFinalSubmit}
                selectedDepartments={selectedDepartments}
                setSelectedDepartments={setSelectedDepartments}
                selectedFacilities={selectedFacilities}
                setSelectedFacilities={setSelectedFacilities}
                selectedLevels={selectedLevels}
                setSelectedLevels={setSelectedLevels}
                institutionImages={institutionImages}
                setInstitutionImages={setInstitutionImages}
                existingImageUrls={existingImageUrls}
                onRemoveExistingImage={index =>
                  setExistingImageUrls(prev =>
                    prev.filter((_, i) => i !== index)
                  )
                }
                brochureFile={brochureFile}
                setBrochureFile={setBrochureFile}
                existingBrochureUrl={existingBrochureUrl}
                onRemoveExistingBrochure={() => setExistingBrochureUrl("")}
                isModal={isModal}
                onCancel={onCancel}
                maxInstitutionImages={MAX_INSTITUTION_IMAGES}
                isEditMode={isEditMode}
              />
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
